import { useMemo } from 'react';

export default function useValidationCompta({ allDocuments, calculateMontantTTC }) {
  const getAllAcomptesESS = useMemo(() => {
    const allAcomptes = [];
    allDocuments.forEach(doc => {
      if (doc.paiements_partiels && doc.paiements_partiels.length > 0) {
        doc.paiements_partiels.forEach((paiement, index) => {
          allAcomptes.push({
            ...paiement,
            docId: doc.id,
            docReference: doc.numero_police || doc.id,
            docName: doc.raison_sociale,
            docSiret: doc.siret,
            acompteIndex: index,
            doc: doc,
            reference: `RCS-ECH-${doc.numero_police || doc.id}-${String(index + 1).padStart(2, '0')}`
          });
        });
      }
    });
    return allAcomptes;
  }, [allDocuments]);

  const calculatedComptaStats = useMemo(() => {
    const stats = {
      totalFacture: 0,
      totalEncaisse: 0,
      totalAcomptes: 0,
      totalEcheances: 0,
      acomptesRecus: 0,
      acomptesProgrammes: 0,
      sepaCore: { count: 0, montant: 0 },
      sepaB2B: { count: 0, montant: 0 },
      cb: { count: 0, montant: 0 },
      virement: { count: 0, montant: 0 },
      cheque: { count: 0, montant: 0 },
      especes: { count: 0, montant: 0 },
      contributionsAcquittees: 0,
      contributionsEnAttente: 0,
      montantAcquitte: 0,
      montantEnAttente: 0,
      // Données pour graphiques
      paiementsParMois: {},
      paiementsParMode: {}
    };

    // Fonction helper pour catégoriser le mode de paiement
    const categorizePaymentMode = (mode, montant) => {
      const modeLower = (mode || 'virement').toLowerCase();
      if (modeLower.includes('sepa') && modeLower.includes('b2b')) {
        stats.sepaB2B.count++;
        stats.sepaB2B.montant += montant;
      } else if (modeLower.includes('sepa') || modeLower.includes('prelevement')) {
        stats.sepaCore.count++;
        stats.sepaCore.montant += montant;
      } else if (modeLower.includes('cb') || modeLower.includes('card') || modeLower.includes('carte') || modeLower.includes('stripe')) {
        stats.cb.count++;
        stats.cb.montant += montant;
      } else if (modeLower.includes('cheque') || modeLower.includes('chèque')) {
        stats.cheque.count++;
        stats.cheque.montant += montant;
      } else if (modeLower.includes('espece') || modeLower.includes('espèce') || modeLower.includes('cash')) {
        stats.especes.count++;
        stats.especes.montant += montant;
      } else {
        // Par défaut: virement
        stats.virement.count++;
        stats.virement.montant += montant;
      }
    };

    allDocuments.forEach(doc => {
      const montantTTC = calculateMontantTTC(doc);
      stats.totalFacture += montantTTC;
      
      if (doc.paiement_valide || doc.est_solde) {
        stats.contributionsAcquittees++;
        stats.montantAcquitte += montantTTC;
        stats.totalEncaisse += montantTTC;
        
        // Catégoriser le paiement du document validé
        const modePaiement = doc.mode_paiement || doc.paiement?.mode || 'virement';
        categorizePaymentMode(modePaiement, montantTTC);
        
        // Par mois pour les documents validés
        const datePaiement = doc.date_validation || doc.date_paiement || doc.updated_at;
        if (datePaiement) {
          const mois = new Date(datePaiement).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
          if (!stats.paiementsParMois[mois]) {
            stats.paiementsParMois[mois] = { encaisse: 0, facture: 0 };
          }
          stats.paiementsParMois[mois].encaisse += montantTTC;
        }
      } else {
        stats.contributionsEnAttente++;
        stats.montantEnAttente += montantTTC;
      }

      // Acomptes / Paiements partiels
      if (doc.paiements_partiels && doc.paiements_partiels.length > 0) {
        doc.paiements_partiels.forEach(p => {
          const montant = p.amount || p.montant || 0;
          stats.totalAcomptes += montant;
          
          if (p.is_scheduled || p.statut === 'programme') {
            stats.acomptesProgrammes++;
          } else {
            stats.acomptesRecus++;
            // Catégoriser les paiements partiels REÇUS (pas les programmés)
            const mode = p.method || p.mode_paiement || 'virement';
            // Note: On ne duplique pas - les paiements partiels sont déjà comptés si doc est soldé
            if (!doc.paiement_valide && !doc.est_solde) {
              categorizePaymentMode(mode, montant);
            }
          }

          // Par mois pour les paiements partiels
          if (p.date && !p.is_scheduled && p.statut !== 'programme') {
            const mois = new Date(p.date).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
            if (!stats.paiementsParMois[mois]) {
              stats.paiementsParMois[mois] = { encaisse: 0, facture: 0 };
            }
            // Éviter double comptage si déjà soldé
            if (!doc.paiement_valide && !doc.est_solde) {
              stats.paiementsParMois[mois].encaisse += montant;
            }
          }
        });
      }

      // Échéanciers
      if (doc.echeancier && doc.echeancier.length > 0) {
        stats.totalEcheances += doc.echeancier.length;
      }
    });

    return stats;
  }, [allDocuments, calculateMontantTTC]);

  return { getAllAcomptesESS, calculatedComptaStats };
}
